// Three-way merge and leaf diff of `{ name, settings }` documents (spec 6.13 items 8 and 9): the Settings tab
// merges the user's edits with settings someone else saved meanwhile, field by field.
import type { JsonValue } from '../api/types';
import { stableStringify } from './json';

type Json = JsonValue | undefined;

function isObject(value: Json): value is { [key: string]: JsonValue } {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

export function jsonEqual(a: Json, b: Json): boolean {
  if (a === undefined || b === undefined) return a === b;
  return stableStringify(a) === stableStringify(b);
}

function escape(key: string): string {
  return key.replace(/~/g, '~0').replace(/\//g, '~1');
}

/** A field both sides changed to different values: the user's value is kept. */
export interface MergeConflict {
  path: string;
  mine: Json;
  theirs: Json;
}

export interface MergeResult<T> {
  merged: T;
  conflicts: MergeConflict[];
}

function merge(base: Json, mine: Json, theirs: Json, path: string, conflicts: MergeConflict[]): Json {
  if (jsonEqual(mine, theirs)) return mine;
  if (jsonEqual(mine, base)) return theirs;
  if (jsonEqual(theirs, base)) return mine;
  if (isObject(mine) && isObject(theirs)) {
    const baseObject = isObject(base) ? base : {};
    const result: { [key: string]: JsonValue } = {};
    const keys = new Set([...Object.keys(theirs), ...Object.keys(mine), ...Object.keys(baseObject)]);
    for (const key of keys) {
      const value = merge(baseObject[key], mine[key], theirs[key], `${path}/${escape(key)}`, conflicts);
      if (value !== undefined) result[key] = value;
    }
    return result;
  }
  conflicts.push({ path, mine, theirs });
  return mine;
}

/**
 * Merges the user's document (`mine`) and the server's (`theirs`), both derived from `base`: a field only one side
 * changed takes that side's value; a field both changed differently keeps mine and is reported. Objects are
 * merged member by member; arrays and scalars are leaves.
 */
export function mergeThreeWay<T>(base: T, mine: T, theirs: T): MergeResult<T> {
  const conflicts: MergeConflict[] = [];
  const merged = merge(base as Json, mine as Json, theirs as Json, '', conflicts);
  return { merged: merged as T, conflicts };
}

export interface LeafChange {
  path: string;
  before: Json;
  after: Json;
}

/** Changed leaves (JSON Pointers) between two documents; objects are walked, arrays and scalars are leaves. */
export function diffLeaves(before: Json, after: Json, path = ''): LeafChange[] {
  if (jsonEqual(before, after)) return [];
  if (isObject(before) && isObject(after)) {
    const keys = [...new Set([...Object.keys(before), ...Object.keys(after)])];
    return keys.flatMap((key) => diffLeaves(before[key], after[key], `${path}/${escape(key)}`));
  }
  if (isObject(before) && after === undefined)
    return Object.keys(before).flatMap((key) => diffLeaves(before[key], undefined, `${path}/${escape(key)}`));
  if (isObject(after) && before === undefined)
    return Object.keys(after).flatMap((key) => diffLeaves(undefined, after[key], `${path}/${escape(key)}`));
  return [{ path, before, after }];
}
